import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { api, errMsg, calcPrice, getPublicSettings, todayIST } from "@/lib/api";
import { useLang, actLabel } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const pyDay = (d) => String((d.getDay() + 6) % 7);

const SlotGrid = ({ slots, start, dur, onPick }) => {
  const inRange = (h) => start != null && h >= start && h < start + dur;
  if (!slots.length) return <p className="text-sm text-slate-500" data-testid="no-slots">No slots on this day.</p>;
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="slot-grid">
      {slots.map((s) => (
        <button key={s.hour} type="button" disabled={s.status !== "available"} onClick={() => onPick(s.hour)} data-testid={`slot-${s.time}`}
          className={`rounded-lg border py-2 font-mono text-xs transition-colors ${inRange(s.hour) ? "bg-[#0D5C46] text-white border-[#0D5C46]" : s.status === "available" ? "bg-white hover:border-[#0D5C46]" : "bg-slate-100 text-slate-400 line-through cursor-not-allowed"}`}>
          {s.time}
        </button>
      ))}
    </div>
  );
};

export const BookingPanel = ({ c, acts }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [settings, setSettings] = useState(null);
  const [activity, setActivity] = useState(c.activities[0]);
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState([]);
  const [start, setStart] = useState(null);
  const [dur, setDur] = useState(2);
  const [place, setPlace] = useState("");
  const [notes, setNotes] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPublicSettings().then(setSettings).catch(() => {});
  }, []);

  const ymd = day ? format(day, "yyyy-MM-dd") : null;
  const loadSlots = () => api.get(`/companions/${c.id}/availability`, { params: { date: ymd } }).then((r) => setSlots(r.data.slots));
  useEffect(() => {
    if (ymd) {
      setStart(null);
      loadSlots().catch(() => setSlots([]));
    }
  }, [ymd]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = useMemo(() => new Date(`${todayIST()}T00:00:00`), []);
  const disabled = (d) => d < today || !c.weekly_availability[pyDay(d)] || c.blocked_dates.includes(format(d, "yyyy-MM-dd"));
  const rangeOk = start != null && [...Array(dur).keys()].every((i) => slots.find((s) => s.hour === start + i)?.status === "available");
  const price = calcPrice(c.hourly_rate, dur, settings);
  const canSubmit = rangeOk && place.trim().length >= 3 && agree && !busy;

  const submit = async () => {
    if (!user) return nav(`/login?next=${encodeURIComponent(loc.pathname)}`);
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { companion_id: c.id, activity, date: ymd, start_time: `${String(start).padStart(2, "0")}:00`, duration_hours: dur, meeting_place: place, notes: notes || null, agree_policy: agree });
      toast.success(`Booking ${data.ref} created. Complete payment to hold it.`);
      nav(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      loadSlots().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-6 space-y-5 shadow-xl shadow-emerald-900/5" data-testid="booking-panel">
      <div>
        <label className="text-sm font-semibold">{t("select_activity")}</label>
        <Select value={activity} onValueChange={setActivity}>
          <SelectTrigger className="mt-1.5" data-testid="booking-activity-select"><SelectValue /></SelectTrigger>
          <SelectContent>{c.activities.map((s) => <SelectItem key={s} value={s}>{actLabel(acts.find((a) => a.slug === s), lang) || s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-semibold">{t("select_date")} <span className="font-normal text-slate-500 text-xs">(IST)</span></label>
        <div className="mt-1.5 rounded-xl border flex justify-center" data-testid="booking-calendar">
          <Calendar mode="single" selected={day} onSelect={setDay} disabled={disabled} />
        </div>
      </div>
      {day && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-semibold">{t("select_time")}</label>
            <Select value={String(dur)} onValueChange={(v) => setDur(Number(v))}>
              <SelectTrigger className="w-32" data-testid="booking-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6].map((h) => <SelectItem key={h} value={String(h)}>{h} {t("hours")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <SlotGrid slots={slots} start={start} dur={dur} onPick={setStart} />
          {start != null && !rangeOk && <p className="text-xs text-rose-600" data-testid="slot-range-error">The selected duration overlaps an unavailable slot.</p>}
          {rangeOk && <p className="text-xs text-emerald-700 font-semibold" data-testid="availability-ok">✓ Available for {dur}h from {String(start).padStart(2, "0")}:00</p>}
        </div>
      )}
      <Input data-testid="booking-meeting-place-input" placeholder={`${t("meeting_place")} — e.g. café, mall, cinema`} value={place} onChange={(e) => setPlace(e.target.value)} maxLength={160} />
      <Textarea data-testid="booking-notes-input" placeholder={t("notes")} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
      <PriceBreakdown p={price} hours={dur} rate={c.hourly_rate} />
      <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer">
        <Checkbox data-testid="booking-agree-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
        {t("agree_policy")}
      </label>
      {user && user.role !== "customer" ? (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="booking-role-warning">Only customer accounts can make bookings.</p>
      ) : (
        <Button data-testid="book-now-btn" className="w-full rounded-full h-12 text-base" disabled={user ? !canSubmit : false} onClick={submit}>
          {busy ? <Loader2 className="animate-spin" /> : user ? t("continue_payment") : t("login_to_book")}
        </Button>
      )}
      <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#0D5C46]" />Payment is in cash at the meetup — nothing is charged online. Requests not accepted before the start time expire.</p>
    </div>
  );
};
